import React, { useEffect, useState, useCallback, useRef } from 'react'

import sidebarLogoWhite from '@/assets/images/logos/sidebar.logo-white.png'
import { useTranslation } from '@/core/i18n/useTranslation'

// Generate a random delay between min and max (in milliseconds)
const getRandomDelay = (min: number, max: number) => {
	return Math.floor(Math.random() * (max - min + 1)) + min
}

interface RegisterStepLoadingProps {
	onSimulationComplete?: () => void
}

export const RegisterStepLoading: React.FC<RegisterStepLoadingProps> = ({
	onSimulationComplete,
}) => {
	const { t } = useTranslation('auth')
	const [currentMessageIndex, setCurrentMessageIndex] = useState(0)
	const [isAnimating, setIsAnimating] = useState(false)
	const hasCompletedRef = useRef(false)

	// Get the loading messages array from translations
	const loadingMessages = t('register.loadingMessages', {
		returnObjects: true,
	}) as string[]

	const advanceToNextMessage = useCallback(() => {
		if (currentMessageIndex < loadingMessages.length - 1) {
			// Start fade out animation
			setIsAnimating(true)

			// After fade out, change message and fade in
			setTimeout(() => {
				setCurrentMessageIndex((prev) => prev + 1)
				setIsAnimating(false)
			}, 300) // Duration of fade out animation
		}
	}, [currentMessageIndex, loadingMessages.length])

	// Handle message progression
	useEffect(() => {
		// Don't set timer if we're on the last message
		if (currentMessageIndex >= loadingMessages.length - 1) {
			// Wait a bit on the last message, then call onSimulationComplete
			if (!hasCompletedRef.current && onSimulationComplete) {
				hasCompletedRef.current = true
				const finalDelay = getRandomDelay(2000, 3000)
				const timer = setTimeout(() => {
					onSimulationComplete()
				}, finalDelay)
				return () => clearTimeout(timer)
			}
			return
		}

		// Random delay between 2 and 4 seconds for each message transition
		const delay = getRandomDelay(2000, 4000)
		const timer = setTimeout(advanceToNextMessage, delay)

		return () => clearTimeout(timer)
	}, [
		currentMessageIndex,
		loadingMessages.length,
		advanceToNextMessage,
		onSimulationComplete,
	])

	if (!loadingMessages || !Array.isArray(loadingMessages)) {
		return null
	}

	return (
		<div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
			{/* Logo */}
			<div className="mb-6">
				<img src={sidebarLogoWhite} alt="Brandtrack" className="h-12" />
			</div>

			{/* Loading Text with fade animation */}
			{/* Using Grid to stack messages and prevent layout shifts/overlapping issues */}
			<div className="grid grid-cols-1 grid-rows-1 place-items-center max-w-sm px-4 w-full">
				{loadingMessages.map((message, index) => (
					<p
						key={index}
						className={`col-start-1 row-start-1 text-white/70 text-base text-center transition-opacity duration-300 ${
							index === currentMessageIndex && !isAnimating
								? 'opacity-100'
								: 'opacity-0'
						}`}
					>
						{message}
					</p>
				))}
			</div>

			{/* Progress dots indicator */}
			<div className="flex gap-2 mt-6">
				{loadingMessages.map((_, index) => (
					<div
						key={index}
						className={`h-2 rounded-full transition-all duration-300 ${
							index === currentMessageIndex
								? 'w-6 bg-white'
								: index < currentMessageIndex
									? 'w-2 bg-white/60'
									: 'w-2 bg-white/30'
						}`}
					/>
				))}
			</div>
		</div>
	)
}
